import React from 'react';

/**
 * Iconos de restaurante dibujados a mano para los avatares.
 *
 * <b>Por qué propios y no de AntD:</b> el set de AntD sólo tiene café, manzana,
 * fuego, tienda y regalo de este mundo — no hay gorro de chef, cubiertos, taco ni
 * charola. Con cinco no se arma un catálogo temático, y rellenar con cohetes y
 * trofeos es justo lo genérico que este rediseño busca evitar.
 *
 * Trazo abierto (fill none + stroke currentColor) para que hereden el color del
 * avatar y encajen con los iconos outlined de AntD que conviven en la app. El
 * tamaño va en 1em: se escalan solos con el disco que los contiene.
 */
const Icon: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <svg
    viewBox="0 0 24 24"
    width="1em"
    height="1em"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {children}
  </svg>
);

/** Gorro de chef */
export const ChefHatIcon = () => (
  <Icon>
    <path d="M7.5 14.5C4.5 14.5 4 10 7.2 9.3 6.8 5.8 10.5 4.2 12 6.6c1.5-2.4 5.2-.8 4.8 2.7 3.2.7 2.7 5.2-.3 5.2" />
    <path d="M7.5 14.5v4.6h9v-4.6" />
    <path d="M7.5 17.2h9" />
  </Icon>
);

/** Cubiertos */
export const CutleryIcon = () => (
  <Icon>
    <path d="M7 3.5V8M10.5 3.5V8" />
    <path d="M7 8c0 1.4.8 2.1 1.75 2.1S10.5 9.4 10.5 8" />
    <path d="M8.75 10.1V20.5" />
    <path d="M16.5 20.5V3.5c2.5 2 2.5 8.2 0 10.2" />
  </Icon>
);

/** Taco */
export const TacoIcon = () => (
  <Icon>
    <path d="M4 11.5C4 19 20 19 20 11.5" />
    <path d="M3 11.5h18" />
    <path d="M7.4 9.6C7.4 7.1 11.4 7.1 11.4 9.6" />
    <path d="M12.6 9.6c0-2.5 4-2.5 4 0" />
  </Icon>
);

/** Rebanada de pizza */
export const PizzaIcon = () => (
  <Icon>
    <path d="M12 3.5 19.5 19c-3 1.5-12 1.5-15 0z" />
    <circle cx="12" cy="10.8" r="1.05" />
    <circle cx="9.6" cy="15.4" r="1.05" />
    <circle cx="14.4" cy="15.4" r="1.05" />
  </Icon>
);

/** Copa */
export const WineIcon = () => (
  <Icon>
    <path d="M7.5 3.5h9l-.7 5.2c-.4 2.3-7.2 2.3-7.6 0z" />
    <path d="M12 11v7.6" />
    <path d="M8.5 20.6h7" />
  </Icon>
);

/** Taza */
export const CupIcon = () => (
  <Icon>
    <path d="M4.5 7.5h11v5.4c0 5.5-11 5.5-11 0z" />
    <path d="M15.5 9h1.9c1.7 0 1.7 4.2 0 4.2h-1.9" />
    <path d="M8 3.4v1.9M12 3.4v1.9" />
  </Icon>
);

/** Chile */
export const ChiliIcon = () => (
  <Icon>
    <path d="M15.6 8.6c1.4 5.5-3.1 10.4-8.6 10.4C6.6 13.5 10.1 8.6 15.6 8.6z" />
    <path d="M15.6 8.6c.2-2 1.2-3.3 2.8-3.8" />
  </Icon>
);

/** Pastelito */
export const CupcakeIcon = () => (
  <Icon>
    <path d="M6.5 12.6h11l-1.3 7.2c-.1.5-.5.8-1 .8H8.8c-.5 0-.9-.3-1-.8z" />
    <path d="M6.5 12.6C6.5 7.2 17.5 7.2 17.5 12.6" />
    <circle cx="12" cy="5" r="1.4" />
  </Icon>
);

/** Charola con campana */
export const ClocheIcon = () => (
  <Icon>
    <path d="M4 16.4C4 7.2 20 7.2 20 16.4" />
    <path d="M2.5 16.4h19" />
    <path d="M12 9.4V7.4" />
    <circle cx="12" cy="6.1" r="1.1" />
  </Icon>
);

/** Sartén */
export const PanIcon = () => (
  <Icon>
    <path d="M3.5 11.6C3.5 18.8 16.5 18.8 16.5 11.6z" />
    <path d="M2.5 11.6h15" />
    <path d="m17.5 11.6 4-1.6" />
  </Icon>
);

/** Helado */
export const IceCreamIcon = () => (
  <Icon>
    <path d="M7.5 10.2C7.5 4.3 16.5 4.3 16.5 10.2z" />
    <path d="M6.4 10.2h11.2" />
    <path d="m6.9 10.2 5.1 10.4 5.1-10.4" />
  </Icon>
);

/** Fuego / parrilla */
export const FlameIcon = () => (
  <Icon>
    <path d="M12 3.4c3.5 4 5.4 6.3 5.4 9.3 0 3-2.4 5.4-5.4 5.4s-5.4-2.4-5.4-5.4c0-1.8.8-3.2 2-4.4.4 1.2 1.2 1.8 2 1.8-.8-2.4-.3-4.6 1.4-6.7z" />
  </Icon>
);
